import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import EditableTitle from '../../components/widgets/EditableTitle';
import css from './SessionList.css';

const SessionList = ({ sessions, currentSessionId, onSessionSelect, onSessionDelete, onSessionUpdate, isLoading }) => {
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');

    const onDelete = (e, sessionId) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this conversation?')) {
            onSessionDelete(sessionId);
        }
    };

    const onTitleEdit = (e, sessionId) => {
        e.stopPropagation();
        const session = sessions.find(s => s.session_id === sessionId);
        if (session) {
            setEditingTitle(session.title);
            setEditingSessionId(sessionId);
        }
    };

    const onTitleChange = (sessionId, newTitle) => setEditingTitle(newTitle);

    const handleTitleChangeComplete = async sessionId => {
        // Save the title when editing is complete (blur or Enter)
        const trimmedTitle = editingTitle.trim();

        if (!trimmedTitle) {
            // If empty, just exit edit mode without saving
            setEditingSessionId(null);
            setEditingTitle('');
            return;
        }

        // Exit edit mode immediately
        setEditingSessionId(null);
        setEditingTitle('');

        // Call the API - optimistic update is handled in the parent hook
        if (onSessionUpdate) {
            await onSessionUpdate(sessionId, { title: trimmedTitle });
        }
    };

    const onSessionItemClick = (e, sessionId) => {
        // Prevent triggering session select when in edit mode
        if (editingSessionId === sessionId) return;

        onSessionSelect(sessionId);
    };

    if (isLoading) {
        return (
            <div className={css.sessionListContainer}>
                <div className={css.sessionListLoading}>Loading sessions...</div>
            </div>
        );
    }

    if (!sessions || sessions.length === 0) {
        return (
            <div className={css.sessionListContainer}>
                <div className={css.sessionListEmpty}>No conversations yet</div>
            </div>
        );
    }

    return (
        <div className={css.sessionListContainer}>
            <div className={css.sessionList}>
                {sessions.map(session => {
                    const isActive = session.session_id === currentSessionId;
                    const isEditing = editingSessionId === session.session_id;

                    return (
                        <div
                            key={session.session_id}
                            className={classNames(css.sessionItem, { [css.active]: isActive, [css.edit]: isEditing })}
                            onClick={e => onSessionItemClick(e, session.session_id)}
                        >
                            <div className={css.sessionItemContent}>
                                <div className={css.sessionItemTitle}>
                                    {isEditing ? (
                                        <EditableTitle
                                            value={editingTitle}
                                            placeholder="Untitled conversation"
                                            onTextChanged={value => onTitleChange(session.session_id, value)}
                                            onTextChangeComplete={() => handleTitleChangeComplete(session.session_id)}
                                            containerClass={css.editableTitleContainer}
                                        />
                                    ) : (
                                        <div className={css.sessionItemTitleText}>{session.title}</div>
                                    )}
                                </div>
                                <div className={css.sessionItemActions}>
                                    <Icon
                                        name="edit"
                                        className={css.sessionItemActionButton}
                                        onClick={e => onTitleEdit(e, session.session_id)}
                                        hoverText="Edit conversation title"
                                    />
                                    <Icon
                                        name="trash"
                                        className={css.sessionItemActionButton}
                                        onClick={e => onDelete(e, session.session_id)}
                                        hoverText="Delete conversation"
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

SessionList.propTypes = {
    sessions: PropTypes.arrayOf(
        PropTypes.shape({
            session_id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            updated_at: PropTypes.string.isRequired,
            message_count: PropTypes.number.isRequired,
        })
    ).isRequired,
    currentSessionId: PropTypes.string,
    onSessionSelect: PropTypes.func.isRequired,
    onSessionDelete: PropTypes.func.isRequired,
    onSessionUpdate: PropTypes.func,
    isLoading: PropTypes.bool,
};

export default SessionList;
